import { Button } from "@instacover/ui"
import cuid2, { isCuid } from "@paralleldrive/cuid2"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans } from "react-i18next"
import { z } from "zod"

import ArrowButton from "@/components/base/ArrowButton"
import { CameraButton } from "@/components/base/CameraButton"
import ImageWrapper from "@/components/base/ImageWrapper"
import ImagesRowWithDetails from "@/components/ImagesRowWithDetails"
import { WallGraphic } from "@/components/wall/WallGraphic"
import { db, upsertDetailToDB } from "@/database/database"
import { DetailSlim } from "@/database/helpers/tables"
import { usePhotoById } from "@/database/hooks"
import { useImagePicker } from "@/helpers/camera/useImagePicker"
import { useSession } from "@/helpers/SessionProvider"
import { wallPathParams } from "@/helpers/zodSchemes"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import PhotoDetailLayout from "@/sections/layouts/PhotoDetailLayout"
import { useModelPredict } from "@/services/model/model"
import { PredictResponse } from "@/services/model/modelTypes"
import { isDetail, isPhoto } from "@/utils/types"

const wallDetailSearchParamsSchema = z.object({
  substep: z.number().optional(),
  currentPhotoId: z.string().optional(),
})

const wallDetailParams = wallPathParams.extend({
  wallDetailId: z.string().refine(isCuid),
})

export const Route = createFileRoute("/$sessionId/_session/_steps/wall/$wallNumber/$wallId_/wallDetail_/$wallDetailId")(
  {
    component: RouteComponent,
    validateSearch: (search) => wallDetailSearchParamsSchema.parse(search),
    params: {
      parse: wallDetailParams.parse,
    },
  },
)

function RouteComponent() {
  const { sessionId, wallId, wallDetailId, wallNumber } = Route.useParams()
  const { substep, currentPhotoId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { session } = useSession()

  const [subStep, setSubStep] = useState(substep ?? 1)

  const { data: wallPhoto } = usePhotoById({ photoId: wallId })

  const { files, select, reset, photoGeolocation } = useImagePicker()
  const file = files?.at(0)

  const currentPhoto = useMemo(() => {
    if (currentPhotoId === wallPhoto?.id) {
      return wallPhoto
    }

    if (!currentPhotoId || currentPhotoId === wallDetailId) {
      return wallPhoto?.details.find((detail) => detail.id === wallDetailId)
    }

    return wallPhoto?.details.find((detail) => detail.id === currentPhotoId)
  }, [currentPhotoId, wallPhoto, wallDetailId])

  const uploadDetail = useCallback(
    async (file: File, modelResult: PredictResponse) => {
      if (!wallPhoto) return

      // Save photo to IndexedDB
      await upsertDetailToDB({
        gps: photoGeolocation
          ? {
              accuracy: photoGeolocation.accuracy,
              latitude: photoGeolocation.latitude,
              longitude: photoGeolocation.longitude,
            }
          : undefined,
        sessionId,
        instahomePhotoId: wallPhoto.id,
        file,
        id: wallDetailId,
        modelResult,
      })
      navigate({ to: ".", search: { currentPhotoId: wallDetailId } })
    },
    [wallPhoto, photoGeolocation, sessionId, wallDetailId, navigate],
  )

  const { data: modelResult } = useModelPredict(file)

  const [hasUploaded, setHasUploaded] = useState(false)
  useEffect(() => {
    if (!file || hasUploaded || !modelResult) return

    uploadDetail(file, modelResult)
    setHasUploaded(true)
    navigate({ to: ".", search: { currentPhotoId: wallDetailId, substep: undefined } })
  }, [uploadDetail, file, hasUploaded, navigate, wallDetailId, modelResult])

  const onAddAnotherAngle = useCallback(() => {
    reset()
    setHasUploaded(false)
    navigate({
      to: "/$sessionId/wall/$wallNumber/$wallId/wallDetail/$wallDetailId",
      params: {
        wallDetailId: cuid2.createId(),
      },
      search: {
        substep: 2,
        currentPhotoId: undefined,
      },
    })
  }, [navigate, reset])

  const onDone = useCallback(() => {
    // Current wall isnt the last one -> navigate to next wall
    if (Number(wallNumber) < session.numberOfWalls) {
      const newWallNumber = Number(wallNumber) + 1
      const newWallId = cuid2.createId()

      navigate({
        to: "/$sessionId/wall/$wallNumber/$wallId",
        params: { sessionId, wallNumber: newWallNumber.toString(), wallId: newWallId },
      })
      reset()
      return
    }

    navigate({
      to: "/$sessionId/addNewWall",
      params: { sessionId },
    })
    reset()
  }, [navigate, reset, session.numberOfWalls, wallNumber, sessionId])

  const onRetakePhoto = useCallback(async () => {
    // Keep the current wallId in the URL — a new id will be assigned inside
    // replaceMainPhoto only after the new photo is actually saved. This avoids
    // a stale wallId in the URL if the user cancels the camera.
    reset()
    navigate({
      to: "/$sessionId/wall/$wallNumber/$wallId",
      params: {
        wallId,
      },
      search: {
        retakePhoto: true,
      },
    })
  }, [navigate, reset, wallId])

  const onDeleteDetail = useCallback(
    async (detail: DetailSlim) => {
      navigate({ to: ".", search: { currentPhotoId: wallId } })
      await db.detail.markAsDeleted(detail.id)
    },
    [navigate, wallId],
  )

  if (currentPhoto) {
    return (
      <PhotoDetailLayout
        aboveHeading={
          <>
            {wallPhoto && <ImagesRowWithDetails photo={wallPhoto} activePhotoId={currentPhotoId} />}

            {isDetail(currentPhoto) && (
              <ImageWrapper photo={currentPhoto} alt="Wall Detail" onDelete={() => onDeleteDetail(currentPhoto)} />
            )}
            {isPhoto(currentPhoto) && <ImageWrapper photo={currentPhoto} alt="Wall Detail" onRetake={onRetakePhoto} />}
          </>
        }
        heading={{
          i18nKey: "wallDetail.headingAfterPhoto",
          values: { wallNumber },
        }}
        description={{
          i18nKey: "wallDetail.descriptionAfterPhoto",
          components: {
            span: <span className="text-ic-primary font-bold uppercase" />,
          },
        }}
        buttons={
          <div className="flex w-full flex-col justify-center gap-2">
            <Button type="button" variant="primary" onClick={onDone} data-testid="wall-detail-done-btn">
              <Trans i18nKey="general.done" />
            </Button>

            <Button type="button" variant="secondary" onClick={onAddAnotherAngle}>
              <Trans i18nKey="wallDetail.addAnotherDetail" />
            </Button>
          </div>
        }
      />
    )
  }

  /* Photo not taken yet */
  return (
    <BaseLayout
      aboveHeading={<WallGraphic type={subStep === 1 ? "HouseDetail" : "HouseBlocked"} />}
      heading={{
        i18nKey: `wallDetail.heading${subStep}`,
      }}
      subheading={{
        i18nKey: "general.howTo",
      }}
      description={{
        i18nKey: `wallDetail.description${subStep}`,
        components: {
          span: <span className="text-ic-primary font-bold uppercase" />,
        },
      }}
      buttons={
        <div className="flex w-full">
          {subStep === 1 ? (
            <ArrowButton
              wrapperClassName="ml-auto"
              variant="primary"
              shape="circle"
              onClick={() => setSubStep(2)}
              arrowDirection="right"
              data-testid="wall-detail-next-substep-btn"
            />
          ) : (
            <CameraButton onClick={select} />
          )}
        </div>
      }
    />
  )
}
